import { createContext, useContext, useEffect, useState, useCallback, ReactNode } from "react";
import { corDoPartido } from "../utils/coresPartido";
import { aplicarTemaPartido } from "../utils/temaPartido";

export interface Usuario {
  id: number;
  nome: string;
  email: string;
  role: "admin" | "candidato" | "lideranca" | "assessor";
  lideranca_id: number | null;
  candidato_id: number | null;
  assessor_id: number | null;
  cargo: string | null;
  candidatoNome: string | null;
  partido: string | null;
  senha_provisoria: boolean;
}

interface AuthContextValue {
  user: Usuario | null;
  loading: boolean;
  login: (email: string, senha: string) => Promise<Usuario>;
  logout: () => Promise<void>;
  trocarSenha: (senhaAtual: string, novaSenha: string) => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

async function request(path: string, options?: RequestInit) {
  const res = await fetch(`/api${path}`, {
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    ...options,
  });
  if (res.status === 204) return null;
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw Object.assign(new Error(data.erro || "Erro"), { status: res.status, data });
  return data;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<Usuario | null>(null);
  const [loading, setLoading] = useState(true);

  const refetch = useCallback(async () => {
    try {
      const data = await request("/auth/me");
      setUser(data.usuario);
    } catch {
      setUser(null);
    }
  }, []);

  useEffect(() => {
    let ativo = true;

    async function verificarSessao() {
      await refetch();
      if (ativo) setLoading(false);
    }

    verificarSessao();
    return () => {
      ativo = false;
    };
  }, [refetch]);

  const login = useCallback(async (email: string, senha: string) => {
    const data = await request("/auth/login", {
      method: "POST",
      body: JSON.stringify({ email, senha }),
    });
    setUser(data.usuario);
    return data.usuario;
  }, []);

  const logout = useCallback(async () => {
    await request("/auth/logout", { method: "POST" });
    setUser(null);
  }, []);

  const trocarSenha = useCallback(async (senhaAtual: string, novaSenha: string) => {
    await request("/auth/trocar-senha", {
      method: "POST",
      body: JSON.stringify({ senha_atual: senhaAtual, nova_senha: novaSenha }),
    });
    setUser((u) => (u ? { ...u, senha_provisoria: false } : u));
  }, []);

  // Cor de marca do site segue o partido do candidato da campanha logada —
  // volta ao azul padrão quando não há partido (admin sem campanha, deslogado).
  useEffect(() => {
    aplicarTemaPartido(user?.partido ? corDoPartido(user.partido) : null);
  }, [user?.partido]);

  // Heartbeat — avisa o backend que a aba ainda tá aberta, pra tela de
  // Acessos (admin) saber "por quanto tempo" além do simples login/logout.
  useEffect(() => {
    if (!user) return;
    const enviar = () => request("/auth/heartbeat", { method: "POST" }).catch(() => {});
    enviar();
    const id = setInterval(enviar, 60 * 1000);
    return () => clearInterval(id);
  }, [user?.id]);

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, trocarSenha }}>{children}</AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth precisa estar dentro de <AuthProvider>");
  return ctx;
}
